import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

export default function BarGraph({ skills }) {
  const [ref, inView] = useInView({
    threshold: 0.35,
    triggerOnce: true,
  });
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    if (!inView) {
      return undefined;
    }

    const timers = skills.map((_, index) =>
      setTimeout(() => {
        setCompletedCount(index + 1);
      }, 450 + index * 500),
    );

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [inView, skills]);

  const hasStarted = inView;
  const allComplete = completedCount === skills.length;

  return (
    <motion.div
      ref={ref}
      className="glass rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-blue-950/20"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      viewport={{ once: true }}
    >
      <div className="flex items-center justify-between px-5 py-4 bg-black/40 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-rose-400" />
          <span className="w-3 h-3 rounded-full bg-amber-400" />
          <span className="w-3 h-3 rounded-full bg-emerald-400" />
        </div>
        <p className="text-xs uppercase tracking-[0.28em] text-gray-400">
          skill-compiler.exe
        </p>
        <span className="text-xs text-emerald-400 font-medium">running</span>
      </div>

      <div className="bg-[#0d1117] px-5 py-5 font-mono text-sm">
        <div className="text-gray-500 mb-4">$ ./install-core-stack</div>

        <div className="space-y-3">
          {skills.map((skill, index) => {
            const isReady = index < completedCount;
            const isInstalling =
              hasStarted && !allComplete && index === completedCount;

            return (
              <motion.div
                key={skill.name}
                className="rounded-2xl border border-white/5 bg-white/[0.03] px-4 py-3"
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-gray-200">
                      <span className={`text-base ${skill.accent}`}>
                        {skill.icon}
                      </span>
                      <span className="font-semibold">{skill.command}</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {skill.output}
                    </p>
                  </div>

                  {isReady ? (
                    <motion.span
                      className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${skill.badgeClass}`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.25 }}
                    >
                      ready
                    </motion.span>
                  ) : isInstalling ? (
                    <div className="shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-300">
                      <span className="flex items-center gap-1.5">
                        <span>installing</span>
                        <motion.span
                          className="flex gap-0.5"
                          initial={{ opacity: 1 }}
                        >
                          {[0, 1, 2].map((dot) => (
                            <motion.span
                              key={dot}
                              className="h-1 w-1 rounded-full bg-amber-300"
                              animate={{ opacity: [0.25, 1, 0.25] }}
                              transition={{
                                duration: 0.8,
                                repeat: Infinity,
                                delay: dot * 0.15,
                              }}
                            />
                          ))}
                        </motion.span>
                      </span>
                    </div>
                  ) : (
                    <span className="shrink-0 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                      queued
                    </span>
                  )}
                </div>

                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/[0.04]">
                  <motion.div
                    className={`h-full rounded-full ${
                      isReady
                        ? "bg-gradient-to-r from-emerald-400 to-cyan-400"
                        : isInstalling
                          ? "bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400"
                          : "bg-transparent"
                    }`}
                    initial={{ width: 0 }}
                    animate={{
                      width: isReady ? "100%" : isInstalling ? "78%" : "0%",
                      x: isInstalling ? ["-12%", "8%", "-12%"] : 0,
                    }}
                    transition={{
                      width: {
                        duration: isReady ? 0.35 : 0.45,
                        ease: "easeOut",
                      },
                      x: {
                        duration: 1.1,
                        repeat: isInstalling ? Infinity : 0,
                        ease: "easeInOut",
                      },
                    }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div
          className={`mt-4 flex items-center gap-2 ${
            allComplete ? "text-emerald-400" : "text-amber-300"
          }`}
        >
          <span>{allComplete ? "build complete" : "installing modules"}</span>
          <motion.span
            className={`inline-block h-4 w-2 ${
              allComplete ? "bg-emerald-400" : "bg-amber-300"
            }`}
            animate={{ opacity: [1, 0, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
        </div>
      </div>
    </motion.div>
  );
}
